import React, { useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  TextField,
  Button,
  Typography,
  Alert,
  Container,
  InputAdornment,
  Fade,
  Zoom,
} from '@mui/material';
import { EmailOutlined, ArrowBack } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import { authService } from '../services/authService';
import logo from '../assets/logo.png';

const ForgotPassword: React.FC = () => {
  const [email, setEmail] = useState('');
  const [error, setError] = useState('');
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);
  const [resetToken, setResetToken] = useState('');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess(false);
    setLoading(true);

    try {
      const response = await authService.forgotPassword(email);
      setSuccess(true);
      // Em desenvolvimento, o backend retorna o token
      if (response.data.resetToken) {
        setResetToken(response.data.resetToken);
      }
    } catch (err: any) {
      setError(err.response?.data?.message || 'Erro ao solicitar recuperação de senha. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          backgroundImage: 'url("https://images.unsplash.com/photo-1465146633011-14f8e0781093?q=80&w=2076&auto=format&fit=crop")',
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          filter: 'brightness(0.7)',
          zIndex: 0,
        },
        '&::after': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'linear-gradient(135deg, rgba(46, 125, 50, 0.85) 0%, rgba(76, 175, 80, 0.75) 50%, rgba(129, 199, 132, 0.65) 100%)',
          zIndex: 1,
        },
      }}
    >
      <Container maxWidth="sm" sx={{ position: 'relative', zIndex: 2 }}>
        <Zoom in timeout={500}>
          <Card
            elevation={24}
            sx={{
              borderRadius: 4,
              overflow: 'visible',
              background: 'rgba(255, 255, 255, 0.98)',
              backdropFilter: 'blur(20px)',
              position: 'relative',
              boxShadow: '0 20px 60px rgba(0, 0, 0, 0.3)',
            }}
          >
            <Box
              sx={{
                position: 'absolute',
                top: -50,
                left: '50%',
                transform: 'translateX(-50%)',
                width: 100,
                height: 100,
                borderRadius: '50%',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: '0 10px 30px rgba(76, 175, 80, 0.5)',
                border: '4px solid white',
                overflow: 'hidden',
                background: 'white',
              }}
            >
              <img
                src={logo}
                alt="Semente Diária"
                style={{
                  width: '130%',
                  height: '130%',
                  objectFit: 'cover',
                }}
              />
            </Box>

            <CardContent sx={{ p: 5, pt: 8 }}>
              <Box sx={{ textAlign: 'center', mb: 4 }}>
                <Typography
                  variant="h4"
                  component="h1"
                  gutterBottom
                  sx={{
                    fontWeight: 800,
                    background: 'linear-gradient(135deg, #2E7D32 0%, #4CAF50 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    mb: 1,
                  }}
                >
                  Recuperar Senha
                </Typography>
                <Typography
                  variant="body1"
                  color="text.secondary"
                  sx={{
                    fontWeight: 500,
                    letterSpacing: '0.5px',
                  }}
                >
                  Digite seu email para receber instruções
                </Typography>
              </Box>

              <Fade in timeout={700}>
                <Box>
                  {error && (
                    <Alert
                      severity="error"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          fontSize: '1.5rem',
                        },
                      }}
                    >
                      {error}
                    </Alert>
                  )}

                  {success && (
                    <Alert
                      severity="success"
                      sx={{
                        mb: 3,
                        borderRadius: 2,
                        '& .MuiAlert-icon': {
                          fontSize: '1.5rem',
                        },
                      }}
                    >
                      Instruções de recuperação foram enviadas para seu email!
                      {resetToken && (
                        <Box sx={{ mt: 2, p: 2, bgcolor: 'rgba(0,0,0,0.05)', borderRadius: 1 }}>
                          <Typography variant="caption" sx={{ fontWeight: 600 }}>
                            Token de recuperação (desenvolvimento):
                          </Typography>
                          <Typography
                            variant="body2"
                            sx={{
                              fontFamily: 'monospace',
                              wordBreak: 'break-all',
                              mt: 1,
                            }}
                          >
                            {resetToken}
                          </Typography>
                          <Typography variant="caption" sx={{ display: 'block', mt: 1 }}>
                            Use este token na página de redefinição de senha.
                          </Typography>
                        </Box>
                      )}
                    </Alert>
                  )}

                  {!success && (
                    <form onSubmit={handleSubmit}>
                      <TextField
                        fullWidth
                        label="Email"
                        type="email"
                        value={email}
                        onChange={(e) => setEmail(e.target.value)}
                        required
                        autoFocus
                        InputLabelProps={{
                          shrink: true,
                        }}
                        InputProps={{
                          startAdornment: (
                            <InputAdornment position="start">
                              <EmailOutlined sx={{ color: 'primary.main' }} />
                            </InputAdornment>
                          ),
                        }}
                        sx={{
                          mb: 3,
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            '&:hover fieldset': {
                              borderColor: 'primary.main',
                            },
                            '&.Mui-focused fieldset': {
                              borderWidth: 2,
                            },
                          },
                        }}
                      />
                      <Button
                        fullWidth
                        type="submit"
                        variant="contained"
                        size="large"
                        disabled={loading}
                        sx={{
                          mt: 2,
                          py: 1.8,
                          borderRadius: 2,
                          fontSize: '1.1rem',
                          fontWeight: 600,
                          textTransform: 'none',
                          color: '#ffffff',
                          background: 'linear-gradient(135deg, #4CAF50 0%, #66BB6A 100%)',
                          boxShadow: '0 4px 12px rgba(76, 175, 80, 0.3)',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            background: 'linear-gradient(135deg, #43A047 0%, #5CB860 100%)',
                            boxShadow: '0 6px 20px rgba(76, 175, 80, 0.4)',
                            transform: 'translateY(-2px)',
                          },
                          '&:active': {
                            transform: 'translateY(0)',
                          },
                          '&.Mui-disabled': {
                            background: 'rgba(0, 0, 0, 0.12)',
                          },
                        }}
                      >
                        {loading ? 'Enviando...' : 'Enviar Instruções'}
                      </Button>
                    </form>
                  )}
                </Box>
              </Fade>

              <Box sx={{ mt: 4, textAlign: 'center' }}>
                <Link
                  to="/login"
                  style={{
                    color: '#4CAF50',
                    textDecoration: 'none',
                    fontWeight: 600,
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: '4px',
                  }}
                >
                  <ArrowBack fontSize="small" />
                  Voltar para o login
                </Link>
              </Box>

              {success && (
                <Box sx={{ mt: 3, textAlign: 'center' }}>
                  <Typography variant="body2" color="text.secondary">
                    Já tem o token?{' '}
                    <Link
                      to="/reset-password"
                      style={{
                        color: '#4CAF50',
                        textDecoration: 'none',
                        fontWeight: 600,
                      }}
                    >
                      Redefinir senha
                    </Link>
                  </Typography>
                </Box>
              )}
            </CardContent>
          </Card>
        </Zoom>
      </Container>
    </Box>
  );
};

export default ForgotPassword;
